import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import {
    Modal,
    ConfirmModal,
    Button,
    FormGroup,
    FormLabel,
    FormControl,
    FormSelect
} from '../components/adminModals';
import { userService, categoryAdminService, UserItem, UserRoleItem, RoleItem, CreateUserPayload, UpdateUserPayload, UpdateUserRolePayload, CategoryAdminItem, Category, CreateUpdateCategoryAdminPayload } from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import { useAuth } from '../../../auth/hooks/auth';
import Pagination from '../components/Pagination';
import { Plus, Mail, AlertCircle } from 'lucide-react';
// import ExportButton from '../../sharedComponents/ExportButton';

function ManageUser() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    // Only roles 3 (System Admin) and 5 (TAG Chairman) can access the Category Admins tab
    const canManageCategoryAdmins = [3, 5].includes(user?.roleId ?? -1);

    // ── Tab state ──────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<'users' | 'categoryAdmins'>('users');

    // ── User Management state ──────────────────────────────────────────────────
    const [userData, setUserData] = useState<UserItem[]>([]);
    const [roleData, setRoleData] = useState<RoleItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserItem | null>(null);
    const [formData, setFormData] = useState({
        userId: '',
        firstName: '',
        lastName: '',
        email: '',
        roleId: ''
    });
    const [statusChangeData, setStatusChangeData] = useState({
        userId: '',
        status: false,
        userName: ''
    });
    const [isRoleStatusModalOpen, setIsRoleStatusModalOpen] = useState(false);
    const [roleChangeData, setRoleChangeData] = useState({
        userId: '',
        userRoleId: '',
        roleName: '',
        userName: '',
        status: false
    });
    const [roleOptions, setRoleOptions] = useState<UserRoleItem[]>([]);
    const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
    const [roleModalLoading, setRoleModalLoading] = useState(false);
    const [editRoleData, setEditRoleData] = useState({
        userRoleId: '',
        newRoleId: '',
        userName: ''
    });
    const [editRoleOptions, setEditRoleOptions] = useState<UserRoleItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // User pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // User filters
    const [filters, setFilters] = useState({
        search: '',
        isActive: null as boolean | null,
        roleId: 0,
        createdFrom: null as string | null,
        createdTo: null as string | null
    });

    // ── Category Admin Management state ───────────────────────────────────────
    const [categoryAdminData, setCategoryAdminData] = useState<CategoryAdminItem[]>([]);
    const [categoryAdminLoading, setCategoryAdminLoading] = useState(false);
    const [categoriesForAdmin, setCategoriesForAdmin] = useState<Category[]>([]);

    const [isEditCategoryAdminOpen, setIsEditCategoryAdminOpen] = useState(false);
    const [isDeleteCategoryAdminOpen, setIsDeleteCategoryAdminOpen] = useState(false);
    const [selectedCategoryAdmin, setSelectedCategoryAdmin] = useState<CategoryAdminItem | null>(null);
    const [categoryAdminSubmitting, setCategoryAdminSubmitting] = useState(false);
    const [categoryAdminModalLoading, setCategoryAdminModalLoading] = useState(false);

    const [editCategoryAdminForm, setEditCategoryAdminForm] = useState({
        id: 0,
        categoryAdminId: '',
        fullName: '',
        categoryId: 0
    });

    // Category admin pagination
    const [caCurrentPage, setCaCurrentPage] = useState(1);
    const [caPageSize, setCaPageSize] = useState(10);
    const [caTotalRecords, setCaTotalRecords] = useState(0);
    const [caTotalPages, setCaTotalPages] = useState(0);

    // Category admin search
    const [caSearch, setCaSearch] = useState('');
    const [caFilterOpen, setCaFilterOpen] = useState(false);

    // ── Bootstrap ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchRoles();
    }, []);

    // User tab: re-fetch on filter/page changes with debounce for search
    useEffect(() => {
        if (activeTab !== 'users') return;
        const handler = setTimeout(() => {
            fetchUsers();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, filters, activeTab]);

    // Category admin tab: re-fetch on search/page changes (only if allowed)
    useEffect(() => {
        if (activeTab !== 'categoryAdmins' || !canManageCategoryAdmins) return;
        const handler = setTimeout(() => {
            fetchCategoryAdmins();
        }, 400);
        return () => clearTimeout(handler);
    }, [caCurrentPage, caPageSize, caSearch, activeTab]);

    // Tab switch triggers immediate fetch
    useEffect(() => {
        if (activeTab === 'users') fetchUsers();
        else if (canManageCategoryAdmins) fetchCategoryAdmins();
    }, [activeTab]);

    // ── User data fetching ─────────────────────────────────────────────────────

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await userService.getAllUsers({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'Username',
                sortDescending: true,
                search: filters.search,
                isActive: filters.isActive,
                roleId: filters.roleId,
                createdFrom: filters.createdFrom,
                createdTo: filters.createdTo
            });

            if (response.isSuccess) {
                setUserData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch users'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching users'
            });
            } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await userService.getUserCreationRoles();
            if (response.isSuccess) {
                setRoleData(response.data);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch roles'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching roles'
            });
            }
    };

    // ── Category Admin data fetching ───────────────────────────────────────────

    const fetchCategoryAdmins = async () => {
        setCategoryAdminLoading(true);
        try {
            const response = await categoryAdminService.getAllCategoryAdmins();
            if (response.isSuccess) {
                const allData = response.data;
                // Client-side search filter
                const filtered = caSearch
                    ? allData.filter(item =>
                        item.fullName.toLowerCase().includes(caSearch.toLowerCase()) ||
                        item.username.toLowerCase().includes(caSearch.toLowerCase()) ||
                        (item.categoryName || '').toLowerCase().includes(caSearch.toLowerCase())
                    )
                    : allData;

                setCaTotalRecords(filtered.length);
                setCaTotalPages(Math.ceil(filtered.length / caPageSize));
                const start = (caCurrentPage - 1) * caPageSize;
                setCategoryAdminData(filtered.slice(start, start + caPageSize));
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch thematic area leaders'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching thematic area leaders'
            });
            } finally {
            setCategoryAdminLoading(false);
        }
    };

    const fetchCategoriesForAdmin = async () => {
        try {
            const response = await categoryAdminService.getCategoriesForCategoryAdmin();
            if (response.isSuccess) {
                setCategoriesForAdmin(response.data);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch thematic areas'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching thematic areas'
            });
            }
    };

    // ── User filter handlers ───────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, search: query }));
        setCurrentPage(1);
    };

    const handleFilterChange = (field: string, value: string | boolean | number | null) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({ search: '', isActive: null, roleId: 0, createdFrom: null, createdTo: null });
        setCurrentPage(1);
    };

    // ── User pagination handlers ───────────────────────────────────────────────

    const handlePageChange = (page: number) => setCurrentPage(page);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    // ── Category admin search/pagination handlers ──────────────────────────────

    const handleCaSearch = (query: string) => {
        setCaSearch(query);
        setCaCurrentPage(1);
    };

    // ── User edit / status handlers ────────────────────────────────────────────

    const handleEdit = async (user: UserItem) => {
        setSelectedUser(user);
        setIsEditModalOpen(true);
        setModalLoading(true);

        try {
            const response = await userService.getUserById(user.userId);
            if (response.isSuccess) {
                const data = response.data;
                const primaryRoleId = data.roles?.[0]?.roleId ?? data.roleId ?? '';
                setFormData({
                    userId: data.userId,
                    firstName: data.person.firstName.trim(),
                    lastName: data.person.lastName.trim(),
                    email: data.person.email,
                    roleId: primaryRoleId ? primaryRoleId.toString() : ''
                });
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch user details'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching user details'
            });
        } finally {
            setModalLoading(false);
        }
    };

    const handleStatusClick = (user: UserItem) => {
        setSelectedUser(user);
        setStatusChangeData({
            userId: user.userId,
            status: !user.active,
            userName: user.person.fullName
        });
        setIsStatusModalOpen(true);
    };

    const handleRoleStatusClick = (user: UserItem) => {
        const availableRoles = user.roles || [];
        const firstRole = availableRoles[0];

        setSelectedUser(user);
        setRoleOptions(availableRoles);
        setRoleChangeData({
            userId: user.userId,
            userRoleId: firstRole?.id || '',
            roleName: firstRole?.role.name || '',
            userName: user.person.fullName,
            status: firstRole ? !firstRole.isActive : false
        });
        setIsRoleStatusModalOpen(true);
    };

    const handleEditRoleClick = async (user: UserItem) => {
        setSelectedUser(user);
        setEditRoleData({ userRoleId: '', newRoleId: '', userName: user.person.fullName });
        setEditRoleOptions([]);
        setIsEditRoleModalOpen(true);
        setRoleModalLoading(true);

        try {
            const response = await userService.getUserById(user.userId);
            if (response.isSuccess) {
                const data = response.data;
                const roles = data.roles || [];
                const firstRole = roles[0];
                setEditRoleOptions(roles);
                setEditRoleData({
                    userRoleId: firstRole?.id || '',
                    newRoleId: '',
                    userName: data.person.fullName
                });
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch user details'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching user details'
            });
        } finally {
            setRoleModalLoading(false);
        }
    };

    const handleUpdateUserRole = async () => {
        if (!editRoleData.userRoleId || !editRoleData.newRoleId) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'Please select a role to assign.'
            });
            return;
        }

        const newRoleId = parseInt(editRoleData.newRoleId);
        if (editRoleOptions.some((r) => r.roleId === newRoleId)) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'This user already has that role. Please choose a different role.'
            });
            return;
        }

        setSubmitting(true);
        try {
            const payload: UpdateUserRolePayload = {
                userRoleId: editRoleData.userRoleId,
                newRoleId
            };

            const response = await userService.updateUserRole(payload);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'User role updated successfully',
                    timer: 3000
                });
                setIsEditRoleModalOpen(false);
                setEditRoleData({ userRoleId: '', newRoleId: '', userName: '' });
                setEditRoleOptions([]);
                setSelectedUser(null);
                await fetchUsers();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to update user role'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while updating the user role'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmRoleStatusChange = async () => {
        if (!roleChangeData.userRoleId) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'Please select a role to update.'
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await userService.activateDeactivateUserRole(roleChangeData.userRoleId, roleChangeData.status);

            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: `User role ${roleChangeData.status ? 'activated' : 'deactivated'} successfully`,
                    timer: 3000
                });
                setIsRoleStatusModalOpen(false);
                setRoleChangeData({ userId: '', userRoleId: '', roleName: '', userName: '', status: false });
                setRoleOptions([]);
                setSelectedUser(null);
                await fetchUsers();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to change role status'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while changing role status'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmStatusChange = async () => {
        setSubmitting(true);
        try {
            const response = await userService.changeStatus(statusChangeData.userId, statusChangeData.status);

            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: `User ${statusChangeData.status ? 'activated' : 'deactivated'} successfully`,
                    timer: 3000
                });
                setIsStatusModalOpen(false);
                setStatusChangeData({ userId: '', status: false, userName: '' });
                setSelectedUser(null);
                await fetchUsers();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to change user status'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while changing user status'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleAddUser = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.roleId) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'Please fill in all required fields'
            });
            return;
        }

        setSubmitting(true);
        try {
            const payload: CreateUserPayload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                roleId: parseInt(formData.roleId)
            };

            const response = await userService.createUser(payload);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'User created successfully',
                    timer: 3000
                });
                setIsAddModalOpen(false);
                setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                await fetchUsers();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to create user'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while creating the user'
            });
            } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateUser = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'Please fill in all required fields'
            });
            return;
        }

        setSubmitting(true);
        try {
            const payload: UpdateUserPayload = {
                userId: formData.userId,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email
            };

            const response = await userService.updateUser(payload);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'User updated successfully',
                    timer: 3000
                });
                setIsEditModalOpen(false);
                setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                setSelectedUser(null);
                await fetchUsers();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to update user'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while updating the user'
            });
            } finally {
            setSubmitting(false);
        }
    };

    // ── Category Admin handlers ────────────────────────────────────────────────

    const handleEditCategoryAdmin = async (item: CategoryAdminItem) => {
        setSelectedCategoryAdmin(item);
        setIsEditCategoryAdminOpen(true);
        setCategoryAdminModalLoading(true);

        setEditCategoryAdminForm({
            id: item.id,
            categoryAdminId: item.categoryAdminId,
            fullName: item.fullName,
            categoryId: item.categoryId || 0
        });

        try {
            await fetchCategoriesForAdmin();
        } finally {
            setCategoryAdminModalLoading(false);
        }
    };

    const handleUpdateCategoryAdmin = async () => {
        if (!editCategoryAdminForm.categoryId) {
            showNotification('warning', {
                title: 'Validation Error',
                message: 'Please select a thematic area'
            });
            return;
        }

        setCategoryAdminSubmitting(true);
        try {
            const payload: CreateUpdateCategoryAdminPayload = {
                id: editCategoryAdminForm.id,
                categoryAdminId: editCategoryAdminForm.categoryAdminId,
                categoryId: editCategoryAdminForm.categoryId
            };

            const response = await categoryAdminService.createUpdateCategoryAdmin(payload);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'Thematic area leader updated successfully',
                    timer: 3000
                });
                setIsEditCategoryAdminOpen(false);
                setSelectedCategoryAdmin(null);
                setEditCategoryAdminForm({ id: 0, categoryAdminId: '', fullName: '', categoryId: 0 });
                await fetchCategoryAdmins();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to update thematic area leader'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while updating the thematic area leader'
            });
            } finally {
            setCategoryAdminSubmitting(false);
        }
    };

    const handleDeleteCategoryAdmin = (item: CategoryAdminItem) => {
        setSelectedCategoryAdmin(item);
        setIsDeleteCategoryAdminOpen(true);
    };

    const handleConfirmDeleteCategoryAdmin = async () => {
        if (!selectedCategoryAdmin) return;
        setCategoryAdminSubmitting(true);
        try {
            const response = await categoryAdminService.deleteCategoryAdmin(selectedCategoryAdmin.id);
            if (response.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: 'Thematic area removed from admin successfully',
                    timer: 3000
                });
                setIsDeleteCategoryAdminOpen(false);
                setSelectedCategoryAdmin(null);
                await fetchCategoryAdmins();
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to delete thematic area leader'
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while deleting the thematic area leader'
            });
            } finally {
            setCategoryAdminSubmitting(false);
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const getInitials = (firstName: string, lastName: string) =>
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

    const getInitialsFromFullName = (fullName: string) => {
        const parts = fullName.trim().split(' ');
        if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
        return fullName.charAt(0).toUpperCase();
    };

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // ── User Table columns ─────────────────────────────────────────────────────

    const userColumns: TableColumn<UserItem>[] = [
        {
            key: 'person',
            label: 'User',
            sortValue: (row) => row.person.fullName,
            render: (value, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {getInitials(row.person.firstName, row.person.lastName)}
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">{row.person.fullName}</div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Mail className="w-3.5 h-3.5" />
                            <span>{row.person.email}</span>
                        </div>
                        {!row.isVerified && (
                            <div className="flex items-center gap-1 text-red-600 font-medium text-xs">
                                <AlertCircle size={12} />
                                Unverified Account
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            key: 'role',
            label: 'Roles',
            sortValue: (row) => row.roles?.map((role) => role.role.name).join(', ') ?? row.role?.name ?? '',
            render: (value, row) => {
                const roles = row.roles || [];
                if (!roles.length) {
                    return <span className="text-gray-500">N/A</span>;
                }

                const visibleRoles = roles.slice(0, 4);

                return (
                    <div className="grid grid-cols-2 gap-1">
                        {visibleRoles.map((userRole) => (
                            <div
                                key={userRole.id}
                                className={`flex items-center gap-2 rounded-full border px-2 py-1 text-[11px] font-semibold ${userRole.isActive ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-100 text-gray-700'}`}
                            >
                                <span className={`h-2.5 w-2.5 rounded-full ${userRole.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                                <span className="truncate">{userRole.role.name}</span>
                            </div>
                        ))}
                        {roles.length > 4 && (
                            <div className="flex items-center justify-center rounded-full border border-gray-200 bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-700">
                                +{roles.length - 4} more
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'active',
            label: 'Active Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} />
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        }
    ];

    const userActions: TableAction<UserItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'Edit User',
                    onClick: handleEdit,
                    className: 'text-blue-600'
                },
                {
                    label: 'Edit User Role',
                    onClick: handleEditRoleClick,
                    className: 'text-blue-600'
                },
                {
                    label: (row: UserItem) => {
                        const roles = row.roles || [];
                        if (roles.length === 1) {
                            return roles[0].isActive ? 'Deactivate User Role' : 'Activate User Role';
                        }
                        return 'Manage User Roles';
                    },
                    onClick: handleRoleStatusClick,
                    className: 'text-orange-600'
                },
                {
                    label: (row: UserItem) => row.active ? 'Deactivate User' : 'Activate User',
                    onClick: handleStatusClick,
                    className: (row: UserItem) => row.active ? 'text-red-600' : 'text-green-600'
                }
            ],
            title: 'Actions'
        }
    ];

    // ── Category Admin Table columns ───────────────────────────────────────────

    const categoryAdminColumns: TableColumn<CategoryAdminItem>[] = [
        {
            key: 'fullName',
            label: 'Admin',
            render: (value, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {getInitialsFromFullName(row.fullName)}
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">{row.fullName}</div>
                        <div className="text-sm text-gray-500">{row.username}</div>
                    </div>
                </div>
            )
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value) => (
                <span className="text-gray-700">
                    {value
                        ? value
                        : <em className="italic text-gray-500">Unassigned</em>
                    }
                </span>
            )
        },
        {
            key: 'active',
            label: 'Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} />
                    {value ? 'Active' : 'Inactive'}
                </span>
            )
        }
    ];

    const categoryAdminActions: TableAction<CategoryAdminItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'Edit Admin Thematic Area',
                    onClick: handleEditCategoryAdmin,
                    className: 'text-blue-600'
                },
                {
                    label: 'Delete Admin Thematic Area',
                    onClick: handleDeleteCategoryAdmin,
                    className: 'text-red-600'
                }
            ],
            title: 'Actions'
        }
    ];

    // ── Filter panel ───────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={filters.isActive === null ? '' : filters.isActive.toString()}
                        onChange={(e) => {
                            const value = e.target.value === '' ? null : e.target.value === 'true';
                            handleFilterChange('isActive', value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                        value={filters.roleId}
                        onChange={(e) => handleFilterChange('roleId', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={0}>All Roles</option>
                        {roleData.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                    <input
                        type="date"
                        value={filters.createdFrom || ''}
                        onChange={(e) => handleFilterChange('createdFrom', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                    <input
                        type="date"
                        value={filters.createdTo || ''}
                        onChange={(e) => handleFilterChange('createdTo', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear all filters
            </button>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">

                    {/* Header */}
                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                User & Role Management
                            </h1>
                            <p className="text-gray-500 text-sm sm:text-base">
                                Manage system access, roles, and permissions.
                            </p>
                        </div>

                        {activeTab === 'users' && (
                            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-start">
                                {/*
                                <ExportButton
                                    reportType={1}
                                    parameters={{
                                        search: filters.search,
                                        isActive: filters.isActive,
                                        roleId: filters.roleId,
                                        createdFrom: filters.createdFrom,
                                        createdTo: filters.createdTo,
                                    }}
                                    reportTitle="Users Report"
                                    buttonText="Export Users"
                                />
                                */}
                                <Button
                                    variant="success"
                                    onClick={() => {
                                        setIsAddModalOpen(true);
                                        setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                                    }}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2"
                                >
                                    <Plus size={16} />
                                    Add New User
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Tabs — only shown to roles 3 and 5 who can access both */}
                    {canManageCategoryAdmins && (
                        <div className="mb-6">
                            <div className="bg-white rounded-xl border border-gray-200 flex">
                                <button
                                    onClick={() => setActiveTab('users')}
                                    className={`flex-1 px-6 py-3 font-medium rounded-l-md transition-colors ${activeTab === 'users' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    User Management
                                </button>
                                <button
                                    onClick={() => setActiveTab('categoryAdmins')}
                                    className={`flex-1 px-6 py-3 font-medium rounded-r-md transition-colors ${activeTab === 'categoryAdmins' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    Thematic Area Member Management
                                </button>
                            </div>
                        </div>
                    )}

                    {/* User Management Tab */}
                    {activeTab === 'users' && (
                        <>
                            <ReusableTable
                                title="User Management"
                                subTitle="View and manage all system users"
                                columns={userColumns}
                                data={userData}
                                actions={userActions}
                                loading={loading}
                                searchPlaceholder="Search users..."
                                onSearch={handleSearch}
                                emptyMessage="No users found. Create your first user!"
                                showSearch={true}
                                showFilter={true}
                                filterPanel={filterPanel}
                                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                                isFilterOpen={isFilterOpen}
                            />
                            {!loading && totalRecords > 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    totalRecords={totalRecords}
                                    pageSize={pageSize}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handlePageSizeChange}
                                />
                            )}
                        </>
                    )}

                    {/* Category Admin Management Tab — gated behind canManageCategoryAdmins */}
                    {activeTab === 'categoryAdmins' && canManageCategoryAdmins && (
                        <>
                            <ReusableTable
                                title="Thematic Area Leader Management"
                                subTitle="View and manage thematic area leader assignments"
                                columns={categoryAdminColumns}
                                data={categoryAdminData}
                                actions={categoryAdminActions}
                                loading={categoryAdminLoading}
                                searchPlaceholder="Search thematic area leaders..."
                                onSearch={handleCaSearch}
                                emptyMessage="No thematic area leaders found."
                                showSearch={true}
                                showFilter={false}
                                onFilterToggle={() => setCaFilterOpen(!caFilterOpen)}
                                isFilterOpen={caFilterOpen}
                            />
                            {!categoryAdminLoading && caTotalRecords > 0 && (
                                <Pagination
                                    currentPage={caCurrentPage}
                                    totalPages={caTotalPages}
                                    totalRecords={caTotalRecords}
                                    pageSize={caPageSize}
                                    onPageChange={setCaCurrentPage}
                                    onPageSizeChange={(size) => { setCaPageSize(size); setCaCurrentPage(1); }}
                                />
                            )}
                        </>
                    )}

                    {/* Add User Modal */}
                    <Modal
                        isOpen={isAddModalOpen}
                        onClose={() => {
                            setIsAddModalOpen(false);
                            setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                        }}
                        title="Add New User"
                        description="Create a new user account and assign a role"
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required>First Name</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    placeholder="Enter first name"
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Last Name</FormLabel>
                                <FormControl
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    placeholder="Enter last name"
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Email</FormLabel>
                                <FormControl
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="user@example.com"
                                    disabled={submitting}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel required>Role</FormLabel>
                                <FormSelect
                                    value={formData.roleId}
                                    onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                                    disabled={submitting}
                                >
                                    <option value="">Select a role</option>
                                    {roleData.map((role) => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </FormSelect>
                            </FormGroup>

                            <div className="flex gap-3 mt-6">
                                <Button
                                    variant="white"
                                    onClick={() => {
                                        setIsAddModalOpen(false);
                                        setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                                    }}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleAddUser}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Spinner />
                                            Adding...
                                        </span>
                                    ) : 'Add User'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Edit User Modal */}
                    <Modal
                        isOpen={isEditModalOpen}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                            setSelectedUser(null);
                            setModalLoading(false);
                        }}
                        title="Edit User"
                        description="Update user account details. Roles are managed separately."
                    >
                        <div>
                            {modalLoading ? (
                                <div className="py-12 flex justify-center items-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <span className="text-gray-600">Loading user details...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <FormGroup>
                                        <FormLabel required>First Name</FormLabel>
                                        <FormControl
                                            type="text"
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            disabled={submitting}
                                            required
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel required>Last Name</FormLabel>
                                        <FormControl
                                            type="text"
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            disabled={submitting}
                                            required
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel required>Email</FormLabel>
                                        <FormControl
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            disabled={submitting || !!selectedUser?.isVerified}
                                            required
                                        />
                                        {selectedUser?.isVerified && (
                                            <p className="mt-1 text-xs text-gray-500">
                                                Email can only be changed for unverified accounts.
                                            </p>
                                        )}
                                    </FormGroup>

                                    <div className="flex gap-3 mt-6">
                                        <Button
                                            variant="white"
                                            onClick={() => {
                                                setIsEditModalOpen(false);
                                                setFormData({ userId: '', firstName: '', lastName: '', email: '', roleId: '' });
                                                setSelectedUser(null);
                                            }}
                                            className="flex-1"
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleUpdateUser}
                                            className="flex-1"
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <Spinner />
                                                    Updating...
                                                </span>
                                            ) : 'Update User'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>

                    {/* Edit User Role Modal */}
                    <Modal
                        isOpen={isEditRoleModalOpen}
                        onClose={() => {
                            setIsEditRoleModalOpen(false);
                            setEditRoleData({ userRoleId: '', newRoleId: '', userName: '' });
                            setEditRoleOptions([]);
                            setSelectedUser(null);
                            setRoleModalLoading(false);
                        }}
                        title="Edit User Role"
                        description={`Change the role assigned to ${editRoleData.userName || 'this user'}.`}
                    >
                        <div>
                            {roleModalLoading ? (
                                <div className="py-12 flex justify-center items-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <span className="text-gray-600">Loading user roles...</span>
                                    </div>
                                </div>
                            ) : editRoleOptions.length === 0 ? (
                                <p className="text-sm text-gray-500">No roles available for this user.</p>
                            ) : (
                                <>
                                    {editRoleOptions.length > 1 ? (
                                        <FormGroup>
                                            <FormLabel required>Current Role</FormLabel>
                                            <FormSelect
                                                value={editRoleData.userRoleId}
                                                onChange={(e) => {
                                                    setEditRoleData({
                                                        ...editRoleData,
                                                        userRoleId: e.target.value,
                                                        newRoleId: ''
                                                    });
                                                }}
                                                disabled={submitting}
                                            >
                                                {editRoleOptions.map((roleOption) => (
                                                    <option key={roleOption.id} value={roleOption.id}>
                                                        {roleOption.role.name}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                        </FormGroup>
                                    ) : (
                                        <FormGroup>
                                            <FormLabel>Current Role</FormLabel>
                                            <FormControl
                                                type="text"
                                                value={editRoleOptions[0]?.role.name || ''}
                                                onChange={() => undefined}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <FormLabel required>New Role</FormLabel>
                                        <FormSelect
                                            value={editRoleData.newRoleId}
                                            onChange={(e) => setEditRoleData({ ...editRoleData, newRoleId: e.target.value })}
                                            disabled={submitting}
                                        >
                                            <option value="">Select a role</option>
                                            {roleData
                                                .filter((role) => !editRoleOptions.some((r) => r.roleId === role.id))
                                                .map((role) => (
                                                    <option key={role.id} value={role.id}>{role.name}</option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>

                                    <div className="flex gap-3 mt-6">
                                        <Button
                                            variant="white"
                                            onClick={() => {
                                                setIsEditRoleModalOpen(false);
                                                setEditRoleData({ userRoleId: '', newRoleId: '', userName: '' });
                                                setEditRoleOptions([]);
                                                setSelectedUser(null);
                                            }}
                                            className="flex-1"
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleUpdateUserRole}
                                            className="flex-1"
                                            disabled={submitting || !editRoleData.userRoleId || !editRoleData.newRoleId}
                                        >
                                            {submitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <Spinner />
                                                    Updating...
                                                </span>
                                            ) : 'Update Role'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>

                    {/* Status Change Confirmation Modal */}
                    <ConfirmModal
                        isOpen={isStatusModalOpen}
                        onClose={() => {
                            setIsStatusModalOpen(false);
                            setStatusChangeData({ userId: '', status: false, userName: '' });
                            setSelectedUser(null);
                        }}
                        onConfirm={handleConfirmStatusChange}
                        title={`${statusChangeData.status ? 'Activate' : 'Deactivate'} User`}
                        message={`Are you sure you want to ${statusChangeData.status ? 'activate' : 'deactivate'} "${statusChangeData.userName}"? ${!statusChangeData.status ? 'This will revoke their system access.' : 'This will restore their system access.'}`}
                        confirmText={statusChangeData.status ? 'Activate' : 'Deactivate'}
                        cancelText="Cancel"
                        variant={statusChangeData.status ? 'info' : 'danger'}
                        isSubmitting={submitting}
                    />

                    <Modal
                        isOpen={isRoleStatusModalOpen}
                        onClose={() => {
                            setIsRoleStatusModalOpen(false);
                            setRoleChangeData({ userId: '', userRoleId: '', roleName: '', userName: '', status: false });
                            setRoleOptions([]);
                            setSelectedUser(null);
                        }}
                        title={`${roleChangeData.status ? 'Activate' : 'Deactivate'} User Role`}
                        description={`Select a role to ${roleChangeData.status ? 'activate' : 'deactivate'} for ${roleChangeData.userName}.`}
                    >
                        <div>
                            {roleOptions.length === 0 ? (
                                <p className="text-sm text-gray-500">No roles available for this user.</p>
                            ) : (
                                <>
                                    <FormGroup>
                                        <FormLabel required>Role</FormLabel>
                                        <FormSelect
                                            value={roleChangeData.userRoleId}
                                            onChange={(e) => {
                                                const userRoleId = e.target.value;
                                                const selected = roleOptions.find((item) => item.id === userRoleId);
                                                if (selected) {
                                                    setRoleChangeData({
                                                        userId: roleChangeData.userId,
                                                        userRoleId: selected.id,
                                                        roleName: selected.role.name,
                                                        userName: roleChangeData.userName,
                                                        status: !selected.isActive
                                                    });
                                                }
                                            }}
                                        >
                                            {roleOptions.map((roleOption) => (
                                                <option key={roleOption.id} value={roleOption.id}>
                                                    {roleOption.role.name} {roleOption.isActive ? '(Active)' : '(Inactive)'}
                                                </option>
                                            ))}
                                        </FormSelect>
                                    </FormGroup>
                                    {/* <div className="mt-4 text-sm text-gray-600">
                                        Current action: <span className="font-semibold">{roleChangeData.status ? 'Activate role' : 'Deactivate role'}</span>
                                    </div> */}
                                </>
                            )}
                            <div className="flex gap-3 mt-6">
                                <Button
                                    variant="white"
                                    onClick={() => {
                                        setIsRoleStatusModalOpen(false);
                                        setRoleChangeData({ userId: '', userRoleId: '', roleName: '', userName: '', status: false });
                                        setRoleOptions([]);
                                        setSelectedUser(null);
                                    }}
                                    className="flex-1"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant={roleChangeData.status ? 'success' : 'danger'}
                                    onClick={handleConfirmRoleStatusChange}
                                    className="flex-1"
                                    disabled={submitting || !roleChangeData.userRoleId}
                                >
                                    {submitting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Spinner />
                                            {roleChangeData.status ? 'Activating...' : 'Deactivating...'}
                                        </span>
                                    ) : roleChangeData.status ? 'Activate Role' : 'Deactivate Role'}
                                </Button>
                            </div>
                        </div>
                    </Modal>

                    {/* Edit Category Admin Modal */}
                    <Modal
                        isOpen={isEditCategoryAdminOpen}
                        onClose={() => {
                            setIsEditCategoryAdminOpen(false);
                            setSelectedCategoryAdmin(null);
                            setEditCategoryAdminForm({ id: 0, categoryAdminId: '', fullName: '', categoryId: 0 });
                            setCategoryAdminModalLoading(false);
                        }}
                        title="Edit Admin Thematic Area"
                        description="Assign or update the thematic area for this administrator"
                    >
                        <div>
                            {categoryAdminModalLoading ? (
                                <div className="py-12 flex justify-center items-center">
                                    <div className="flex flex-col items-center gap-3">
                                        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <span className="text-gray-600">Loading thematic areas...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <FormGroup>
                                        <FormLabel>Admin Name</FormLabel>
                                        <FormControl
                                            type="text"
                                            value={editCategoryAdminForm.fullName}
                                            onChange={() => undefined}
                                            disabled={true}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel required>Thematic Area</FormLabel>
                                        <FormSelect
                                            value={editCategoryAdminForm.categoryId.toString()}
                                            onChange={(e) => setEditCategoryAdminForm({
                                                ...editCategoryAdminForm,
                                                categoryId: parseInt(e.target.value)
                                            })}
                                            disabled={categoryAdminSubmitting}
                                        >
                                            <option value="0">Select a thematic area</option>
                                            {categoriesForAdmin.map((category) => (
                                                <option key={category.id} value={category.id}>{category.name}</option>
                                            ))}
                                        </FormSelect>
                                    </FormGroup>

                                    <div className="flex gap-3 mt-6">
                                        <Button
                                            variant="white"
                                            onClick={() => {
                                                setIsEditCategoryAdminOpen(false);
                                                setSelectedCategoryAdmin(null);
                                                setEditCategoryAdminForm({ id: 0, categoryAdminId: '', fullName: '', categoryId: 0 });
                                            }}
                                            className="flex-1"
                                            disabled={categoryAdminSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleUpdateCategoryAdmin}
                                            className="flex-1"
                                            disabled={categoryAdminSubmitting}
                                        >
                                            {categoryAdminSubmitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <Spinner />
                                                    Updating...
                                                </span>
                                            ) : 'Update'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal>

                    {/* Delete Category Admin Confirmation Modal */}
                    <ConfirmModal
                        isOpen={isDeleteCategoryAdminOpen}
                        onClose={() => {
                            setIsDeleteCategoryAdminOpen(false);
                            setSelectedCategoryAdmin(null);
                        }}
                        onConfirm={handleConfirmDeleteCategoryAdmin}
                        title="Delete Admin Thematic Area"
                        message={`Are you sure you want to remove the thematic area assignment for "${selectedCategoryAdmin?.fullName}"? This will unassign their current thematic area.`}
                        confirmText="Delete"
                        cancelText="Cancel"
                        variant="danger"
                        isSubmitting={categoryAdminSubmitting}
                    />
                </div>
            </div>
        </div>
    );
}

export default ManageUser;